import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { navigateToMember, navigateToSite } from '@flexo/resolvers';
import { timeFormat } from 'd3-time-format';
import { capitalizeFirstLetter } from '@flexo/general';

const formatDate = timeFormat('%d/%m/%Y');

export interface DetailsListItem {
  title: string;
  value: string;
}

export interface DetailsListConfig {
  title: string;
  subtitle?: string;
  isClickable?: boolean;
  dataList: DetailsListItem[];
  onTitleClick?: () => void;
  isInactive?: boolean;
  isDocked?: boolean;
}

export type DetailsListType = 'communityPaymentsList' | 'memberDetails' | 'siteDetails' | 'communityDetails' | 'siteMembers' | 'memberSites' | 'MemberDetailsParameters' | 'CommunityDetailsParameters';

interface UseDetailsListDataReturn {
  isGroup: boolean;
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  singleConfig: DetailsListConfig | null;
  groupConfigs: DetailsListConfig[];
  totalItems: number;
  hasExpandCollapse: boolean;
}

/**
 * Custom hook to handle DetailsList data processing and state management
 * @param type - The type of details list to render
 * @returns Object with processed data and utility functions
 */
export function useDetailsListData(type: DetailsListType, data: any = null): UseDetailsListDataReturn {
  const { t } = useTranslation();
  const modules = useSelector((state: any) => state.modules);
  const metadata = useSelector((state: any) => state.metadata);
  
  const [expanded, setExpanded] = useState(false);

  // Determine if this is a single item or group
  const isGroup = useMemo(() => ['siteMembers', 'memberSites'].includes(type), [type]);

  // Single configuration creation
  const singleConfig = useMemo(() => {
    if (isGroup) return null;

    const selectedEntity = metadata?.selectedEntity as any;

    switch (type) {
      case 'memberDetails':
        return createMemberDetailsConfig(selectedEntity, data, t, metadata);
      case 'siteDetails':
        return createSiteDetailsConfig(selectedEntity, data, t);
      case 'communityDetails':
      case 'CommunityDetailsParameters':
        return createCommunityDetailsConfig(selectedEntity, data, t, metadata);
      default:
        throw new Error(`Unknown single type: ${type}`);
    }
  }, [type, isGroup, metadata, t, data]);

  // Group configurations creation
  const groupConfigs = useMemo(() => {
    if (!isGroup) return [];

    switch (type) {
      case 'siteMembers':
        return createSiteMembersConfigs(metadata, t);
      case 'memberSites':
        return createMemberSitesConfigs(metadata, t);
      default:
        throw new Error(`Unknown group type: ${type}`);
    }
  }, [type, isGroup, metadata, t]);

  const totalItems = useMemo(() => (groupConfigs?.length || 0), [groupConfigs]);
  const hasExpandCollapse = useMemo(() => type === 'memberSites' && totalItems > 4, [type, totalItems]);

  return {
    isGroup,
    expanded,
    setExpanded,
    singleConfig,
    groupConfigs,
    totalItems,
    hasExpandCollapse,
  };
}

// Helper functions for creating configurations
function createMemberDetailsConfig(selectedEntity: any, data: any, t: any, metadata: any): DetailsListConfig {
  function getMemberName(member: any) {
    return `${member?.memberFirstName} ${member?.memberLastName}` || '';
  }

  function getMemberSites() {
    return ((metadata?.entities as any)?.site?.filter((site: any) => site.memberId === selectedEntity?.memberId) || [])?.length || 0;
  }

  const titleKey = data?.title || 'widgets.memberDetailsParameters.title';
  const fieldsConfig = (data?.fields || {}) as Record<string, string>;
  const shownFields: string[] = Array.isArray(data?.shownFields) ? data.shownFields : [];

  const getValueForField = (fieldKey: string): string => {
    switch (fieldKey) {
      case 'name': {
        const fullName = `${selectedEntity?.memberFirstName || ''} ${selectedEntity?.memberLastName || ''}`.trim();
        return fullName ? capitalizeFirstLetter(fullName) : '-';
      }
      case 'startDate':
        return selectedEntity?.startDate
          ? formatDate(new Date(selectedEntity.startDate))
          : '-';
      case 'memberId':
      case 'clientId':
        return selectedEntity?.memberExternalId || '-';
      default: {
        const value = selectedEntity?.[fieldKey as keyof typeof selectedEntity];
        return value === undefined || value === null || value === ''
          ? '-'
          : String(value);
      }
    }
  };

  const dataList: DetailsListItem[] = [
    ...shownFields.map((fieldKey) => ({
      title: t(fieldsConfig?.[fieldKey] || fieldKey),
      value: getValueForField(fieldKey),
    })),
    // Optional trailing metric from config
    ...(data?.sitesNumber
      ? [{ title: t(data.sitesNumber), value: getMemberSites().toString() }]
      : []),
  ];

  return {
    title: t(titleKey),
    subtitle: capitalizeFirstLetter(getMemberName(selectedEntity)),
    isClickable: false,
    dataList
  };
}

function createSiteDetailsConfig(selectedEntity: any, data: any, t: any): DetailsListConfig {
  const titleKey = data?.title || 'widgets.siteDetailsParameters.title';

  // Support two shapes:
  // 1) New: data.fields + data.shownFields
  // 2) Legacy: data holds label keys directly (siteName, joinDate, ownership, type, productionBenefits)
  const fieldsConfig: Record<string, string> = data?.fields
    ? (data.fields as Record<string, string>)
    : ((): Record<string, string> => {
        const { title, ...rest } = data || {};
        return (rest || {}) as Record<string, string>;
      })();

  const shownFields: string[] = Array.isArray(data?.shownFields)
    ? (data.shownFields as string[])
    : Object.keys(fieldsConfig);

  const getValueForField = (fieldKey: string): string => {
    switch (fieldKey) {
      case 'siteName':
        return selectedEntity?.siteName || '-';
      case 'joinDate': {
        const startDate = selectedEntity?.joinDate || selectedEntity?.startDate;
        const endDate = selectedEntity?.leaveDate;
        const formattedStart = startDate ? formatDate(new Date(startDate)) : '';
        const formattedEnd = endDate && new Date(endDate) <= new Date()
          ? ` - ${formatDate(new Date(endDate))}`
          : '';
        const result = `${formattedStart}${formattedEnd}`;
        return result || '-';
      }
      case 'type': {
        const typeKey = selectedEntity?.siteType?.[0]?.siteType || 'default';
        return t(`widgets.siteDetailsParameters.${typeKey}`);
      }
      default: {
        const value = selectedEntity?.[fieldKey as keyof typeof selectedEntity];
        return value === undefined || value === null || value === ''
          ? '-'
          : String(value);
      }
    }
  };

  const dataList: DetailsListItem[] = shownFields.map((fieldKey) => ({
    title: t(fieldsConfig?.[fieldKey] || fieldKey),
    value: getValueForField(fieldKey),
  }));

  return {
    title: t(titleKey),
    subtitle: selectedEntity?.pod || '',
    isClickable: false,
    dataList,
  };
}

function createCommunityDetailsConfig(selectedEntity: any, data: any, t: any, metadata: any): DetailsListConfig {
  const titleKey = data?.title || 'widgets.communityDetailsParameters.title';
  const fieldsConfig = (data?.fields || {}) as Record<string, string>;
  const shownFields: string[] = Array.isArray(data?.shownFields) ? data.shownFields : [];

  const getValueForField = (fieldKey: string): string => {
    switch (fieldKey) {
      case 'startDate':
        return selectedEntity?.startDate
          ? formatDate(new Date(selectedEntity.startDate))
          : '-';
      case 'members':
        return (metadata?.entities?.member?.filter((member: any) => member?.communityId === selectedEntity?.communityId) || [])?.length || 0;
      default: {
        const value = selectedEntity?.[fieldKey as keyof typeof selectedEntity];
        return value === undefined || value === null || value === ''
          ? '-'
          : String(value);
      }
    }
  };

  const dataList: DetailsListItem[] = shownFields.map((fieldKey) => ({
    title: t(fieldsConfig?.[fieldKey] || fieldKey),
    value: getValueForField(fieldKey),
  }));

  return {
    title: t(titleKey),
    isClickable: false,
    dataList,
  };
}

function createSiteMembersConfigs(metadata: any, t: any): DetailsListConfig[] {
  const membersList = ([
    ...((metadata?.sortedEntities as any)?.communities || []).reduce((prev: any, next: any) => ([...prev, ...(next?.members || [])]), []),
    ...((metadata as any)?.unassignedMembers || [])
  ])?.filter((member: any) => member?.sites?.some((site: any) => site?.siteId === metadata?.selectedEntityId));

  const { since, client_id, billing_address, phone, email } = t('widgets.siteMembersList', { returnObjects: true }) as any;

  return membersList.map((member: any) => ({
    title: capitalizeFirstLetter(`${member?.memberFirstName} ${member?.memberLastName}`),
    isClickable: true,
    onTitleClick: () => navigateToMember('member', member?.memberId),
    dataList: [
      {
        title: since,
        value: (() => {
          const joinDate = (metadata?.selectedEntity as any)?.joinDate;
          const startDate = (metadata?.selectedEntity as any)?.startDate;
          return joinDate 
            ? formatDate(new Date(joinDate))
            : startDate 
              ? formatDate(new Date(startDate))
              : '-';
        })()
      },
      {
        title: client_id,
        value: member?.memberExternalId || '-'
      },
      {
        title: billing_address,
        value: (!member?.address || !member?.city)
          ? '-'
          : `${member?.address}, ${member?.city}`
      },
      {
        title: phone,
        value: member?.phone || '-'
      },
      {
        title: email,
        value: member?.email || '-'
      }
    ]
  }));
}

function createMemberSitesConfigs(metadata: any, t: any): DetailsListConfig[] {
  const sites = (metadata?.selectedEntity as any)?.sites || [];

  return sites.map((site: any) => ({
    title: site.siteExternalId,
    subtitle: capitalizeFirstLetter(site.siteName),
    isClickable: true,
    isInactive: new Date(site?.endDate) < new Date(),
    onTitleClick: () => navigateToSite('site', site?.siteId),
    dataList: [
      {
        title: t('widgets.memberSitesDetails.address'),
        value: `${site.address}, ${site.city}`
      },
      {
        title: t('widgets.memberSitesDetails.owned'),
        value: `${site.startDate}${new Date(site?.endDate) < new Date() ? ` - ${site?.endDate}` : ''}`
      },
      {
        title: t('widgets.memberSitesDetails.type'),
        value: capitalizeFirstLetter(site.type)
      }
    ]
  }));
}

export default useDetailsListData; 